import { parseSanitizedHtml } from "@/utils/sanitizeHtml";
import { useState, useCallback, useEffect, useMemo } from "react";
import Helpers from "@/Helpers";
import '@style/TestSections.scss';

export default function WordSquaresLegacy(props) {
   const { sectionData, values } = props;
   const currentSection = sectionData?.currentSection;
   const content: Record<string, unknown> = useMemo(
      () => Helpers.getSectionContent(currentSection, sectionData?.lang),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [currentSection?.contents, sectionData?.lang]
   );
   const elements = useMemo(() => Helpers.getSectionElements(content), [content]);
   type WordSquareElement = { id: string; page: number; row: number; words: { term: string }[] };
   const contentElements = useMemo(
      () => Array.isArray(elements?.items) ? elements.items as WordSquareElement[] : [],
      [elements]
   );
   const instructionsHtml = Helpers.getSectionHtmlValue(content, "instructions");
   const instructions2Html = Helpers.getSectionHtmlValue(content, "instructionsSecondary");
   useEffect(() => {
      sectionData.registerSection( 640);
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [currentSection?.name]);

   const [sums, updateSums] = useState([]);

   function onChangeValue(childId, value) {
      sectionData.onAnswer(childId, parseInt(value));
   }
   function updateSum(e) {
      const id = e.target.dataset.id;
      updateSums(prevSums => {
         const newSums = [...prevSums];
         newSums[id] = parseInt(e.target.value);
         onChangeValue(id, parseInt(e.target.value));
         return newSums;
      });
   }
   const getIssueCountForPage = useCallback(function (values, page) {
      // Check if page is provided
      if (!page) {
         console.warn("No page specified for validation");
         return 0;
      }

      // Get all elements for the current page
      const localPageValues = contentElements.filter(pv => pv.page === page);

      // Count total issues - start with 0
      let totalIssueCount = 0;

      localPageValues.forEach(item => {
         const value = values[item.id];

         // Check for missing values (count as an issue)
         if (value === undefined || value <= 0) {
            totalIssueCount++;
         }
      });

      return totalIssueCount;
   }, [contentElements]);

   useEffect(() => {
      const nextCount = Number(sectionData?.nextCount || 0);
      const page = Number(sectionData?.page || 1);
      const issueCount = nextCount > 0 ? getIssueCountForPage(values || {}, page) : 0;
      sectionData?.updateIssueCount?.(issueCount);
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [contentElements, getIssueCountForPage, sectionData?.nextCount, sectionData?.page, sectionData?.updateIssueCount, values]);

   return (
      <div className="WordSquaresLegacy page" >
         <div className="instructions left">{parseSanitizedHtml(instructionsHtml)}</div>

         <div className="content center">
            <table className="wordSquareTable">
               <tbody>
                  {
                     // Group elements by row
                     [...new Set(contentElements.map(item => item.row))].map(rowNum => {
                        // Get all elements for this row
                        const rowElements = contentElements.filter(item => item.row === rowNum);

                        return (
                           <tr key={`row-${rowNum}`}>
                              <td className="left">{Helpers.GetOrdinalWord(rowNum)} Row</td>
                                 {rowElements.map(element => ( // Map over the rowElements
                                 <td key={`element-${element.id}`} className="termCell left">
                                    <div className="termContainer">
                                       <div className="termsColumn">
                                          {element.words.map((word, wordIndex) => (
                                             <div key={`word-${element.id}-${wordIndex}`} className="stackedTerm">
                                                {word.term}
                                             </div>
                                          ))}
                                       </div>
                                       <div className="inputColumn">
                                          <input
                                             type="text"
                                             className="wordInput"
                                             aria-label={`Word square row ${rowNum} value ${element.id}`}
                                             placeholder=""
                                             value={values[element.id] > 0 ? values[element.id] : ""}
                                             onChange={(e) => onChangeValue(element.id, e.target.value)}
                                          />
                                       </div>
                                    </div>
                                 </td>
                              ))}
                           </tr>
                        );
                     })
                  }
                  <tr><td className="center" colSpan={5}>{instructions2Html}</td></tr>
                  <tr>
                     <td className="rowNumber"></td>
                     {[...Array(4)].map((_, i) => (
                        <td key={`sum-${i}`}>
                           <input
                              value={sums[i] || ''}
                              data-id={i}
                              onChange={updateSum}
                              type="number"
                              aria-label={`Word squares sum column ${i + 1}`}
                              placeholder=""
                              className="sumInput floatRight"
                           />
                        </td>
                     ))}
                  </tr>
               </tbody>
            </table>
         </div>
      </div>
   )
}


